import React, { useMemo } from 'react';

import { formatCurrency } from '~libs/dataUtils';
import { getErrorMessage } from '~libs/axiosWrapper';

import { ErrorResponse, Spinner } from '~components';
import Modal from '~components/Modal';
import { Column, TableView } from '~components/table-view-v3';

import PaymentStatusBadge from '~direct-debits/components/payment-batches/PaymentStatusBadge';

import { useGetPaymentBatchClientInvoicesQuery } from '~direct-debits/actions/paymentBatches';
import { formatBatchDate } from '~direct-debits/lib/formatBatchDate';
import { PaymentBatchInvoice } from '~direct-debits/models';

interface ComponentProps {
  isOpen: boolean;
  onClose: () => void;
  batchId: string | null;
  clientId: number | null;
  clientName?: string | null;
  onViewInvoice: (invoiceId: string) => void;
}

const formatInvoiceIdShort = (id: string): string => {
  if (!id) return '—';
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
};

const ClientInvoicesModal: React.FC<ComponentProps> = ({
  isOpen,
  onClose,
  batchId,
  clientId,
  clientName,
  onViewInvoice,
}) => {
  const skip = !isOpen || batchId === null || clientId === null;
  const { data, isFetching, isError, error } = useGetPaymentBatchClientInvoicesQuery(
    { batch_id: batchId ?? '', client_id: clientId ?? 0 },
    { skip },
  );

  const rows = useMemo(
    () => (data?.rows ?? []).map((invoice) => ({ ...invoice })),
    [data?.rows],
  );

  // Inline column factory keeps the modal self-contained — the columns include
  // a "View" link that escalates to the parent so the CoCon invoice detail
  // modal can be opened on top of this one.
  const columns: Column[] = useMemo(
    () => [
      {
        id: 'reference',
        header: 'Invoice',
        type: 'text',
        deriveValue: (row) =>
          (row.reference as string) || formatInvoiceIdShort(row.id as string),
      },
      {
        id: 'invoice_date',
        header: 'Date',
        type: 'text',
        deriveValue: (row) => formatBatchDate(row.invoice_date as string),
        classNames: { header: 'cell-align-center', column: 'cell-align-center' },
      },
      {
        id: 'amount',
        header: 'Amount',
        type: 'text',
        deriveValue: (row) => formatCurrency(row.amount as string),
        classNames: { header: 'cell-align-right', column: 'cell-align-right' },
      },
      {
        id: 'status',
        header: 'Status',
        type: 'custom',
        component: PaymentStatusBadge,
        classNames: { header: 'cell-align-center', column: 'cell-align-center' },
      },
      {
        id: 'actions',
        header: '',
        type: 'custom',
        component: ({ row }: { row: PaymentBatchInvoice }) => (
          <a
            href="#view-invoice"
            role="button"
            data-cy={`dd-payment-batch-invoice-view-${row.id}`}
            onClick={(event) => {
              event.preventDefault();
              onViewInvoice(row.id);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onViewInvoice(row.id);
              }
            }}
            className="link-primary"
          >
            View
          </a>
        ),
        classNames: { header: 'cell-align-right', column: 'cell-align-right' },
      },
    ],
    [onViewInvoice],
  );

  const setIsOpen = (open: boolean) => {
    if (!open) onClose();
  };

  const headerLabel = clientName
    ? `${clientName} — Batch ${batchId ? `${batchId.slice(0, 8)}…` : ''}`
    : 'Client Invoices';

  // ID used to wire `aria-labelledby` from the dialog to the header text — the
  // Reactstrap Modal otherwise has no accessible name.
  const headerId = 'dd-payment-batch-invoices-modal-header';

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      header={<span id={headerId}>{headerLabel}</span>}
      headerClassName="header"
      size="lg"
      includeFooter={false}
      labelledBy={headerId}
      data-cy="dd-payment-batch-invoices-modal"
    >
      <div data-cy="dd-payment-batch-invoices-modal-body">
        {isError ? (
          <ErrorResponse message={getErrorMessage(error)} />
        ) : (
          <>
            {isFetching && !data && (
              <div className="my-3">
                <Spinner loading />
              </div>
            )}
            <TableView
              id="dd-payment-batch-invoices-table"
              columns={columns}
              data={rows}
              isLoading={isFetching}
              emptyMessage="No invoices for this client in this batch"
              readonly
              striped
            />
          </>
        )}
      </div>
    </Modal>
  );
};

export default ClientInvoicesModal;
